import { ApiParams, prepareBaseListApiParams } from '@/api/helpers/apiHelpers';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { prepareResponse, throwResponseError, withPreparedResponse } from '@/helpers/apiHelpers/responseHelpers';
import { IDEMPOTENCY_KEY, idempotencyKeyHandler } from '@/helpers/apiHelpers/requestHelpers';
import { getDashboardSummaryApi } from '@/api/v1/organization/dashboard/getDashboardSummaryApi';
import { getRecentActivityApi } from '@/api/v1/organization/dashboard/getRecentActivityApi';
import { getTeamApi } from '@/api/v1/organization/dashboard/getTeamApi';
import { getTasksApi } from '@/api/v1/organization/dashboard/getTasksApi';
import { getTaskApi } from '@/api/v1/organization/dashboard/getTaskApi';
import { createTaskApi, CreateTaskPayloadType } from '@/api/v1/organization/dashboard/createTaskApi';
import { taskActionApi, TaskActionPayloadType } from '@/api/v1/organization/dashboard/taskActionApi';
import {
  getNotificationsApi,
  getUnreadCountApi,
  markAllNotificationsReadApi,
  markNotificationsReadApi,
} from '@/api/v1/organization/dashboard/notificationsApi';
import { TaskActionType, TaskScopeType, TaskViewType } from '@/types/dashboardTypes';

export const DASHBOARD_QUERY_KEYS = {
  summary: ['dashboard_summary'],
  recent: ['dashboard_recent_activity'],
  team: ['dashboard_team'],
  tasks: 'dashboard_tasks',
  task: 'dashboard_task',
  notifications: ['dashboard_notifications'],
  unread: ['dashboard_notifications_unread'],
} as const;

export type TaskListParamsType = QueryParamsType & { scope?: TaskScopeType; view?: TaskViewType };

export const loadSummary = () => withPreparedResponse(getDashboardSummaryApi);
export const loadRecentActivity = () => withPreparedResponse(getRecentActivityApi);
export const loadTeam = () => withPreparedResponse(getTeamApi);
export const loadTask = (id: string) => withPreparedResponse(() => getTaskApi(id));
export const loadNotifications = () => withPreparedResponse(getNotificationsApi);
export const loadUnreadCount = () => withPreparedResponse(getUnreadCountApi);
export const markRead = (ids: string[]) => withPreparedResponse(() => markNotificationsReadApi(ids));
export const markAllRead = () => withPreparedResponse(markAllNotificationsReadApi);

export const loadTasks = (queryParams: TaskListParamsType) => {
  const params: ApiParams = prepareBaseListApiParams(queryParams);

  if (queryParams.scope) params.scope = queryParams.scope;
  if (queryParams.view) params.view = queryParams.view;

  return withPreparedResponse(() => getTasksApi(params));
};

export const runTaskAction = (id: string, action: TaskActionType, payload?: TaskActionPayloadType) =>
  withPreparedResponse(() => taskActionApi(id, action, payload));

export const createTask = async (payload: CreateTaskPayloadType) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY.CREATE_DASHBOARD_TASK);

  try {
    const response = await createTaskApi(payload, header);

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};
